import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  Timestamp,
  writeBatch,
  QueryDocumentSnapshot,
  DocumentData,
} from 'firebase/firestore';
import { parseCSV, type CalculationResult, type DebitCondition, type MatrixData, type MortalityData } from '@mortality/core';

import { useAuth } from './AuthContext';
import { db } from '../firebaseClient';
import {
  appendPendingHistoryAction,
  clearCachedHistory,
  clearPendingHistoryActions,
  getPendingHistoryActions,
  readCachedHistory,
  removePendingAddAction,
  setPendingHistoryActions,
  writeCachedHistory,
  type PendingHistoryAction,
} from '../storage/localHistory';
import {
  clearCachedMortalityData,
  readCachedMortalityData,
  writeCachedMortalityData,
} from '../storage/mortalityDataCache';
import { useFirestoreSync } from '../hooks/useFirestoreSync';
import { logHistoryCacheServe, logHistoryRemoteServe } from '../lib/syncStats';

const DEFAULT_TABLE_PATHS: Record<'catalog' | 'male' | 'female', string> = {
  catalog: '/data/LSI_Medical_Catalog.csv',
  male: '/data/VBT_2015_Male.csv',
  female: '/data/VBT_2015_Female.csv',
};

const fetchCsvFromPublic = async (path: string): Promise<string> => {
  const response = await fetch(path);
  if (!response.ok) {
    throw new Error(`Failed to fetch ${path}`);
  }
  return response.text();
};

interface MatrixParseResult {
  debitCols: number[];
  ages: number[];
  obj: MatrixData;
}

const matrixFromRows = (rows: string[][]): MatrixParseResult => {
  if (!rows.length) {
    throw new Error('Matrix CSV is empty.');
  }

  const header = rows[0].map(h => h.trim().toLowerCase());
  const ageIdx = header.indexOf('age');
  if (ageIdx !== 0) {
    throw new Error("Matrix CSV must include 'age' as the first column");
  }

  const debitCols = header
    .slice(1)
    .map(v => Number(String(v).replace(/[^0-9.-]/g, '')))
    .filter(v => !Number.isNaN(v));
  const ages: number[] = [];
  const obj: MatrixData = {};

  for (let i = 1; i < rows.length; i += 1) {
    const row = rows[i];
    if (!row?.length) {
      continue;
    }
    const age = Number(row[0]);
    if (Number.isNaN(age)) {
      continue;
    }
    ages.push(age);
    const rec: Record<number, number | null> = {};
    for (let j = 1; j < row.length && j <= debitCols.length; j += 1) {
      const col = debitCols[j - 1];
      const val = Number(String(row[j]).replace(/[^0-9.-]/g, ''));
      rec[col] = Number.isNaN(val) ? null : val;
    }
    obj[age] = rec;
  }

  return { debitCols, ages, obj };
};

const unifyColumns = (maleCols: number[], femaleCols: number[]): number[] => {
  const set = new Set([...maleCols, ...femaleCols]);
  return Array.from(set).sort((a, b) => a - b);
};

const normalizeMatrix = (src: MatrixData, debitColumns: number[]): MatrixData => {
  const out: MatrixData = {};
  for (const age of Object.keys(src)) {
    const a = Number(age);
    const row = src[a] ?? {};
    const rec: Record<number, number | null> = {};
    for (const d of debitColumns) {
      rec[d] = Object.prototype.hasOwnProperty.call(row, d) ? row[d] ?? null : null;
    }
    out[a] = rec;
  }
  return out;
};

interface MortalityContextType {
  data: MortalityData | null;
  loading: boolean;
  error: string | null;
  history: CalculationResult[];
  historyLoading: boolean;
  historyHasMore: boolean;
  historySyncError: string | null;
  syncingHistory: boolean;
  loadFromFiles: (catalog: string, maleMatrix: string, femaleMatrix: string) => Promise<void>;
  reloadDefaults: () => Promise<void>;
  clearData: () => void;
  saveCalculation: (calculation: Omit<CalculationResult, 'id' | 'timestamp'>) => Promise<void>;
  clearHistory: () => Promise<void>;
  deleteCalculation: (id: string) => Promise<void>;
  syncHistoryNow: () => Promise<void>;
  refreshHistory: () => Promise<void>;
  loadMoreHistory: () => Promise<void>;
}

const MortalityContext = createContext<MortalityContextType | undefined>(undefined);

export function useMortalityContext() {
  const context = useContext(MortalityContext);
  if (!context) {
    throw new Error('useMortalityContext must be used within MortalityProvider');
  }
  return context;
}

export function MortalityProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<MortalityData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<CalculationResult[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyHasMore, setHistoryHasMore] = useState(true);
  const [mortalityCacheHydrated, setMortalityCacheHydrated] = useState(false);
  const hasLoadedDefaults = useRef(false);
  const historyCursorRef = useRef<QueryDocumentSnapshot<DocumentData> | null>(null);
  const historyLoadingRef = useRef(false);
  const pendingLocalIdsRef = useRef<Set<string>>(new Set());

  const { user } = useAuth();
  const HISTORY_SYNC_THROTTLE_MS = 30_000;
  const HISTORY_PAGE_SIZE = 25;

  const persistHistoryLocally = useCallback(
    (entries: CalculationResult[]) => {
      if (!user) {
        return;
      }
      writeCachedHistory(user.uid, entries).catch(cacheError => {
        console.error('Failed to cache history locally:', cacheError);
      });
    },
    [user],
  );

  const mapSnapshotToHistory = useCallback((snapshotDocs: Array<{ id: string; data: () => Record<string, unknown> }>): CalculationResult[] => {
    return snapshotDocs.map(docSnap => {
      const dataDoc = docSnap.data();
      const timestampValue = dataDoc.timestamp instanceof Timestamp ? dataDoc.timestamp.toDate() : new Date();
      return {
        id: docSnap.id,
        timestamp: timestampValue,
        age: (dataDoc.age as number) ?? 0,
        gender: (dataDoc.gender as 'male' | 'female') ?? 'male',
        selectedConditions: (dataDoc.selectedConditions as DebitCondition[]) ?? [],
        mostSignificantCondition: (dataDoc.mostSignificantCondition as string | null) ?? null,
        manualAdjustment: (dataDoc.manualAdjustment as number) ?? 0,
        totalDebits: (dataDoc.totalDebits as number) ?? 0,
        effectiveDebits: (dataDoc.effectiveDebits as number) ?? 0,
        riskLevel: (dataDoc.riskLevel as CalculationResult['riskLevel']) ?? null,
        months: (dataDoc.months as number | null) ?? null,
        years: (dataDoc.years as number | null) ?? null,
        expectedAge: (dataDoc.expectedAge as number | null) ?? null,
        usedColumn: (dataDoc.usedColumn as string) ?? '',
      };
    });
  }, []);

  const mergeRemoteHistoryEntries = useCallback(
    (incoming: CalculationResult[], reset: boolean) => {
      setHistory(prev => {
        const localEntries = prev.filter(entry => entry.id.startsWith('local-') && pendingLocalIdsRef.current.has(entry.id));
        const previousRemote = reset ? [] : prev.filter(entry => !entry.id.startsWith('local-'));
        const remoteMap = new Map<string, CalculationResult>();
        previousRemote.forEach(entry => remoteMap.set(entry.id, entry));
        incoming.forEach(entry => remoteMap.set(entry.id, entry));
        const sortedRemote = Array.from(remoteMap.values()).sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
        const combined = [...localEntries, ...sortedRemote];
        persistHistoryLocally(combined);
        return combined;
      });
    },
    [persistHistoryLocally],
  );

  const resetHistoryPagination = useCallback(() => {
    historyCursorRef.current = null;
    setHistoryHasMore(true);
  }, []);

  const fetchHistoryPage = useCallback(
    async (options: { reset?: boolean } = {}) => {
      if (!user) {
        return;
      }

      const { reset = false } = options;
      if (historyLoadingRef.current) {
        return;
      }

      historyLoadingRef.current = true;
      setHistoryLoading(true);
      if (reset) {
        resetHistoryPagination();
      }

      try {
        const historyRef = collection(db, 'users', user.uid, 'history');
        let baseQuery = query(historyRef, orderBy('timestamp', 'desc'), limit(HISTORY_PAGE_SIZE));
        if (historyCursorRef.current) {
          baseQuery = query(historyRef, orderBy('timestamp', 'desc'), startAfter(historyCursorRef.current), limit(HISTORY_PAGE_SIZE));
        }

        const snapshot = await getDocs(baseQuery);
        const docs = snapshot.docs;
        if (docs.length > 0) {
          historyCursorRef.current = docs[docs.length - 1];
          const mapped = mapSnapshotToHistory(docs);
          mergeRemoteHistoryEntries(mapped, reset);
          logHistoryRemoteServe(mapped.length);
          if (docs.length < HISTORY_PAGE_SIZE) {
            setHistoryHasMore(false);
          }
        } else {
          setHistoryHasMore(false);
        }
      } catch (fetchError) {
        console.error('Failed to fetch history page:', fetchError);
      } finally {
        historyLoadingRef.current = false;
        setHistoryLoading(false);
      }
    },
    [HISTORY_PAGE_SIZE, mapSnapshotToHistory, mergeRemoteHistoryEntries, resetHistoryPagination, user],
  );

  const performHistorySync = useCallback(async () => {
    if (!user) {
      return;
    }

    const pendingActions = await getPendingHistoryActions(user.uid);
    if (!pendingActions.length) {
      return;
    }

    const batch = writeBatch(db);
    const historyRef = collection(db, 'users', user.uid, 'history');
    let processedAdds = 0;

    for (const action of pendingActions) {
      switch (action.type) {
        case 'add': {
          const docRef = doc(historyRef);
          batch.set(docRef, {
            ...action.calculation,
            timestamp: Timestamp.fromDate(new Date()),
          });
          processedAdds += 1;
          break;
        }
        case 'delete': {
          batch.delete(doc(historyRef, action.id));
          break;
        }
        case 'clear': {
          const toDelete = await getDocs(query(historyRef, limit(300)));
          toDelete.forEach(docSnap => batch.delete(doc(historyRef, docSnap.id)));
          break;
        }
        default:
          break;
      }
    }

    await batch.commit();
    await clearPendingHistoryActions(user.uid);

    if (processedAdds > 0) {
      await fetchHistoryPage({ reset: true });
    }
  }, [fetchHistoryPage, user]);

  const {
    requestSync,
    syncNow,
    markDirty: markHistoryDirty,
    syncing: historySyncing,
    error: historySyncError,
  } = useFirestoreSync({
    enabled: Boolean(user),
    throttleMs: HISTORY_SYNC_THROTTLE_MS,
    performSync: performHistorySync,
  });

  const syncHistoryNow = useCallback(async () => {
    await syncNow();
  }, [syncNow]);

  const refreshHistory = useCallback(async () => {
    if (!user) {
      return;
    }

    const cached = await readCachedHistory(user.uid);
    if (cached.length) {
      logHistoryCacheServe(cached.length);
      setHistory(cached);
    }

    await fetchHistoryPage({ reset: true });
  }, [fetchHistoryPage, user]);

  const loadMoreHistory = useCallback(async () => {
    if (!user || !historyHasMore) {
      return;
    }
    await fetchHistoryPage();
  }, [fetchHistoryPage, historyHasMore, user]);

  const loadFromFiles = useCallback(
    async (catalogContent: string, maleMatrixContent: string, femaleMatrixContent: string) => {
      setLoading(true);
      setError(null);

      try {
        const catRows = parseCSV(catalogContent);
        const maleRows = parseCSV(maleMatrixContent);
        const femaleRows = parseCSV(femaleMatrixContent);

        const cHead = catRows[0].map(h => h.trim().toLowerCase());
        const idx = {
          id: cHead.indexOf('id'),
          name: cHead.indexOf('name'),
          category: cHead.indexOf('category'),
          notes: cHead.indexOf('notes'),
          debits: cHead.indexOf('debits'),
        };
        if (Object.values(idx).some(v => v === -1)) {
          throw new Error('Catalog CSV must include columns id,name,category,notes,debits.');
        }

        const debitCatalog: DebitCondition[] = catRows
          .slice(1)
          .map(r => {
            const rawDebits = String(r[idx.debits] ?? '').trim();
            const requiresReferral = /unable to calculate/i.test(rawDebits);
            const parsedDebits = Number(rawDebits.replace(/[^0-9.-]/g, ''));

            return {
              id: String(r[idx.id] || '').trim(),
              name: String(r[idx.name] || '').trim(),
              category: String(r[idx.category] || '').trim(),
              notes: String(r[idx.notes] || '').trim(),
              debits: requiresReferral || Number.isNaN(parsedDebits) ? 0 : parsedDebits,
              requiresReferral,
            };
          })
          .filter(x => x.id && x.name);

        const mMale = matrixFromRows(maleRows);
        const mFem = matrixFromRows(femaleRows);
        const debitColumns = unifyColumns(mMale.debitCols, mFem.debitCols);
        const ageMin = Math.max(50, Math.min(...mMale.ages, ...mFem.ages));
        const ageMax = Math.min(105, Math.max(...mMale.ages, ...mFem.ages));

        const matrices = {
          male: normalizeMatrix(mMale.obj, debitColumns),
          female: normalizeMatrix(mFem.obj, debitColumns),
        };

        const mortalityData: MortalityData = {
          debitCatalog,
          matrices,
          debitColumns,
          ageMin,
          ageMax,
        };

        setData(mortalityData);
        writeCachedMortalityData(mortalityData).catch(cacheError => {
          console.error('Failed to cache mortality data:', cacheError);
        });
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load mortality data');
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const loadDefaultTables = useCallback(async () => {
    if (hasLoadedDefaults.current) {
      return;
    }

    hasLoadedDefaults.current = true;

    try {
      const [catalogContent, maleMatrixContent, femaleMatrixContent] = await Promise.all([
        fetchCsvFromPublic(DEFAULT_TABLE_PATHS.catalog),
        fetchCsvFromPublic(DEFAULT_TABLE_PATHS.male),
        fetchCsvFromPublic(DEFAULT_TABLE_PATHS.female),
      ]);

      await loadFromFiles(catalogContent, maleMatrixContent, femaleMatrixContent);
    } catch (assetError) {
      console.error('Failed to load default mortality tables:', assetError);
      setError(prev => prev ?? (assetError instanceof Error ? assetError.message : 'Failed to load default mortality tables.'));
      setLoading(false);
    }
  }, [loadFromFiles]);

  const reloadDefaults = useCallback(async () => {
    hasLoadedDefaults.current = false;
    await loadDefaultTables();
  }, [loadDefaultTables]);

  useEffect(() => {
    let isCancelled = false;

    const hydrateMortalityData = async () => {
      if (mortalityCacheHydrated || data) {
        return;
      }

      try {
        const cached = await readCachedMortalityData();
        if (!isCancelled && cached) {
          setData(cached);
        }
      } catch (cacheError) {
        console.error('Failed to hydrate mortality data cache:', cacheError);
      } finally {
        if (!isCancelled) {
          setMortalityCacheHydrated(true);
        }
      }
    };

    hydrateMortalityData();

    return () => {
      isCancelled = true;
    };
  }, [data, mortalityCacheHydrated]);

  useEffect(() => {
    if (!data && mortalityCacheHydrated) {
      loadDefaultTables();
    }
  }, [data, loadDefaultTables, mortalityCacheHydrated]);

  const saveCalculation = useCallback(
    async (calculation: Omit<CalculationResult, 'id' | 'timestamp'>) => {
      if (!user) {
        throw new Error('You must be signed in to save calculations.');
      }

      const localId = `local-${Date.now()}`;
      const localEntry: CalculationResult = {
        id: localId,
        timestamp: new Date(),
        ...calculation,
      };

      pendingLocalIdsRef.current.add(localId);

      setHistory(prev => {
        const next = [localEntry, ...prev];
        persistHistoryLocally(next);
        return next;
      });

      await appendPendingHistoryAction(user.uid, {
        type: 'add',
        localId,
        calculation,
        createdAt: Date.now(),
      });

      markHistoryDirty();
      void requestSync();
    },
    [markHistoryDirty, persistHistoryLocally, requestSync, user],
  );

  const clearHistory = useCallback(async () => {
    setHistory([]);
    resetHistoryPagination();
    pendingLocalIdsRef.current.clear();

    if (!user) {
      return;
    }

    await clearCachedHistory(user.uid);
    const existingPending = await getPendingHistoryActions(user.uid);
    const filteredPending = existingPending.filter(action => action.type !== 'add');
    filteredPending.push({ type: 'clear', createdAt: Date.now() });
    await setPendingHistoryActions(user.uid, filteredPending);
    markHistoryDirty();
    void requestSync(true);
  }, [markHistoryDirty, requestSync, resetHistoryPagination, user]);

  const deleteCalculation = useCallback(
    async (id: string) => {
      if (!user) {
        return;
      }

      setHistory(prev => {
        const next = prev.filter(entry => entry.id !== id);
        persistHistoryLocally(next);
        return next;
      });

      if (id.startsWith('local-')) {
        pendingLocalIdsRef.current.delete(id);
        await removePendingAddAction(user.uid, id);
        return;
      }

      await appendPendingHistoryAction(user.uid, {
        type: 'delete',
        id,
        createdAt: Date.now(),
      });

      markHistoryDirty();
      void requestSync();
    },
    [markHistoryDirty, persistHistoryLocally, requestSync, user],
  );

  const clearData = () => {
    setData(null);
    setError(null);
    clearCachedMortalityData().catch(cacheError => {
      console.error('Failed to clear mortality data cache:', cacheError);
    });
  };

  useEffect(() => {
    if (!user) {
      setHistory([]);
      resetHistoryPagination();
      pendingLocalIdsRef.current.clear();
    }
  }, [resetHistoryPagination, user]);

  const value = useMemo(
    () => ({
      data,
      loading,
      error,
      history,
      historyLoading,
      historyHasMore,
      historySyncError,
      syncingHistory: historySyncing,
      loadFromFiles,
      reloadDefaults,
      clearData,
      saveCalculation,
      clearHistory,
      deleteCalculation,
      syncHistoryNow,
      refreshHistory,
      loadMoreHistory,
    }),
    [
      clearData,
      clearHistory,
      data,
      deleteCalculation,
      error,
      history,
      historyHasMore,
      historyLoading,
      historySyncError,
      historySyncing,
      loadFromFiles,
      reloadDefaults,
      loadMoreHistory,
      loading,
      refreshHistory,
      saveCalculation,
      syncHistoryNow,
    ],
  );

  return <MortalityContext.Provider value={value}>{children}</MortalityContext.Provider>;
}
